import { handleUnknownMapping } from '@/features/game-window-integration/helpers/generic-helpers';

export const dgcErrorRefs = {
  INSUFFICIENT_FUNDS: 'INSUFFICIENT_FUNDS',
  CONNECTION_ERROR: 'CONNECTION_ERROR',
  GENERIC_ERROR: 'GENERIC_ERROR',
  WAGER_LIMIT: 'WAGER_LIMIT',
  AUTHENTICATION_ERROR: 'AUTHENTICATION_ERROR',
  TOO_MANY_GAMES_OPEN: 'TOO_MANY_GAMES_OPEN',
  LOSS_LIMIT: 'LOSS_LIMIT',
};

export const DGC_ERROR_NAMES = {
  GAMEPLAY_ERROR: 'GamePlayError',
  LOGIN_ERROR: 'LoginError',
  GENERAL_ERROR: 'GeneralError',
};

export const dgcErrorsNames = {
  InsufficientFundsError: dgcErrorRefs.INSUFFICIENT_FUNDS,
  NetworkError: dgcErrorRefs.CONNECTION_ERROR,
};

export const dgcGameplayErrorCodes = {
  708: dgcErrorRefs.GENERIC_ERROR,
  0: dgcErrorRefs.GENERIC_ERROR,
  5002: dgcErrorRefs.GENERIC_ERROR,
  511: dgcErrorRefs.GENERIC_ERROR,
  600150: dgcErrorRefs.GENERIC_ERROR,
  500007: dgcErrorRefs.GENERIC_ERROR,
  100008: dgcErrorRefs.GENERIC_ERROR,
  4: dgcErrorRefs.GENERIC_ERROR,
  11548: dgcErrorRefs.GENERIC_ERROR,
  9: dgcErrorRefs.GENERIC_ERROR,
  212: dgcErrorRefs.AUTHENTICATION_ERROR,
  20003: dgcErrorRefs.AUTHENTICATION_ERROR,
  9003: dgcErrorRefs.AUTHENTICATION_ERROR,
  50062: dgcErrorRefs.WAGER_LIMIT,
  15206: dgcErrorRefs.TOO_MANY_GAMES_OPEN,
  503: dgcErrorRefs.INSUFFICIENT_FUNDS,
  50089: dgcErrorRefs.LOSS_LIMIT,
  211: dgcErrorRefs.CONNECTION_ERROR,
  20013: dgcErrorRefs.CONNECTION_ERROR,
};

export const ERROR_MESSAGES = {
  [dgcErrorRefs.INSUFFICIENT_FUNDS]:
    'Please lower your bet amount or add additional funds to continue.',
  [dgcErrorRefs.GENERIC_ERROR]:
    "We're sorry about this. Please try again or leave your game to go home.",
  [dgcErrorRefs.AUTHENTICATION_ERROR]:
    'Please refresh your game to keep playing. You might see the results for your bet.',
  [dgcErrorRefs.CONNECTION_ERROR]:
    "It's not you, it's us. Please try again or go back home.",
  [dgcErrorRefs.WAGER_LIMIT]: 'The player has reached their wager limit.',
  [dgcErrorRefs.LOSS_LIMIT]: 'The player has reached their loss limit.',
  [dgcErrorRefs.TOO_MANY_GAMES_OPEN]:
    'You have too many games open. Please close one to continue.',
};

const processErrorType = (payloadData) => {
  const { name: errorName, code: errorCode } = payloadData?.Body ?? {};
  const isKnownError = !!(
    errorName &&
    (dgcErrorsNames[errorName] || errorName === DGC_ERROR_NAMES.GAMEPLAY_ERROR)
  );

  if (isKnownError) {
    const isGameplayError = errorName === DGC_ERROR_NAMES.GAMEPLAY_ERROR;
    const isKnownGameplayErrorCode =
      errorCode && dgcGameplayErrorCodes[errorCode];
    if (isGameplayError && isKnownGameplayErrorCode) {
      return dgcGameplayErrorCodes[errorCode];
    }

    if (isGameplayError && !isKnownGameplayErrorCode) {
      handleUnknownMapping(payloadData, `${errorName}_${errorCode}`);
      return dgcGameplayErrorCodes[708]; // Fallback to GENERIC
    }

    return dgcErrorsNames[errorName];
  }

  handleUnknownMapping(payloadData, errorName || errorCode || 'UNKNOWN_ERROR');
  return dgcGameplayErrorCodes[708]; // Fallback to GENERIC
};

const processErrorCode = (payloadData) => {
  const { name: errorName, code: errorCode } = payloadData?.Body ?? {};
  const isKnownError = !!(
    errorName &&
    (dgcErrorsNames[errorName] || errorName === DGC_ERROR_NAMES.GAMEPLAY_ERROR)
  );
  if (isKnownError) {
    const isGameplayError = errorName === DGC_ERROR_NAMES.GAMEPLAY_ERROR;
    const isKnownGameplayErrorCode =
      errorCode && dgcGameplayErrorCodes[errorCode];
    if (isGameplayError && isKnownGameplayErrorCode) {
      return errorCode.toString();
    }
    return errorName;
  }

  return dgcGameplayErrorCodes[708].toString();
};

const processErrorMessage = (payloadData) => {
  const { name: errorName, code: errorCode } = payloadData?.Body ?? {};
  const isKnownError = !!(
    errorName &&
    (dgcErrorsNames[errorName] || errorName === DGC_ERROR_NAMES.GAMEPLAY_ERROR)
  );

  if (isKnownError) {
    const isGameplayError = errorName === DGC_ERROR_NAMES.GAMEPLAY_ERROR;
    const isKnownGameplayErrorCode =
      errorCode && dgcGameplayErrorCodes[errorCode];
    if (isGameplayError && isKnownGameplayErrorCode) {
      return ERROR_MESSAGES[dgcGameplayErrorCodes[errorCode]];
    }
    if (isGameplayError && !isKnownGameplayErrorCode) {
      return ERROR_MESSAGES[dgcGameplayErrorCodes[708]];
    }
    return ERROR_MESSAGES[dgcErrorsNames[errorName]];
  }

  return ERROR_MESSAGES[dgcGameplayErrorCodes[708]];
};

export const processDgcError = (payloadData) => {
  /* Due to DGC inconsistent structure of error messages objects,
  a nominal mapper has been created which relates both the error's name and code 
  to the error type, code and message.
  */
  const { name: bodyName, code: bodyCode } = payloadData?.Body ?? {};
  const useCodeMap =
    Object.values(DGC_ERROR_NAMES).includes(bodyName) &&
    dgcGameplayErrorCodes[bodyCode];
  if (useCodeMap) {
    const errorType = dgcGameplayErrorCodes[bodyCode];
    return {
      errorType,
      errorCode: String(bodyCode),
      errorMessage: ERROR_MESSAGES[errorType],
    };
  }

  const errorType = processErrorType(payloadData);
  let errorCode = processErrorCode(payloadData);

  // Error code must always be a string or it will break the Modal Pop-up in iOS flow
  if (typeof errorCode !== 'string') {
    errorCode = errorCode.toString();
  }
  const errorMessage = processErrorMessage(payloadData);

  return {
    errorType,
    errorCode,
    errorMessage,
  };
};
